import React, { useState, useEffect } from 'react';
import { 
  Facebook, Instagram, Share2, Link, Copy, Check, Settings, 
  ExternalLink, AlertTriangle, Globe, Image as ImageIcon, Twitter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import axiosInstance from '../../api/axios';

const SocialMediaSettings = () => {
  const [settings, setSettings] = useState({
    facebook_page_url: '',
    instagram_handle: '',
    twitter_handle: '',
    share_enabled: true,
    auto_share_new_products: false,
    auto_share_blog_posts: false,
    default_hashtags: '#homeyoursoul #handmade #shop'
  });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axiosInstance.get('/settings/social-media');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log('No social media settings found, using defaults');
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/settings/social-media', settings);
      toast.success('Einstellungen gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success('Link kopiert!');
    setTimeout(() => setCopied(null), 2000);
  };

  const shopUrl = window.location.origin;

  return (
    <div className="min-h-screen py-4 px-3 sm:px-4" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <Share2 className="h-6 w-6" />
            Social Media Integration
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Verbinden Sie Ihren Shop mit Facebook und Instagram
          </p>
        </div>

        {/* Info Box */}
        <Card className="mb-6 bg-blue-50 border-blue-200 rounded-none">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <Globe className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-800 mb-1">Rechtssicher teilen</p>
                <p className="text-blue-700">
                  Mit dieser Integration können Sie Ihre eigenen Produkte und Blog-Beiträge 
                  auf Facebook und Instagram teilen. Es werden nur Inhalte geteilt, die Ihnen gehören.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="links" className="space-y-4">
          <TabsList className="grid grid-cols-3 w-full bg-[#C4B5A4] p-1 rounded-none border-0 gap-1">
            <TabsTrigger 
              value="links" 
              className="text-xs sm:text-sm rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 transition-all py-2"
            >
              <Link className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Profil-</span>Links
            </TabsTrigger>
            <TabsTrigger 
              value="share" 
              className="text-xs sm:text-sm rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 transition-all py-2"
            >
              <Share2 className="h-4 w-4 mr-1 sm:mr-2" />
              Teilen
            </TabsTrigger>
            <TabsTrigger 
              value="settings" 
              className="text-xs sm:text-sm rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 transition-all py-2"
            >
              <Settings className="h-4 w-4 mr-1 sm:mr-2" />
              Einstellungen
            </TabsTrigger>
          </TabsList>

          {/* Profile Links Tab */}
          <TabsContent value="links">
            <Card className="rounded-none">
              <CardHeader>
                <CardTitle className="text-lg">Ihre Social Media Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Facebook className="h-4 w-4 text-blue-600" />
                    Facebook Seiten-URL
                  </Label>
                  <Input
                    value={settings.facebook_page_url}
                    onChange={(e) => setSettings({...settings, facebook_page_url: e.target.value})}
                    placeholder="https://facebook.com/IhreSeite"
                    className="rounded-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Geben Sie die URL Ihrer Facebook-Unternehmensseite ein
                  </p>
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Instagram className="h-4 w-4 text-pink-600" />
                    Instagram Benutzername
                  </Label>
                  <div className="flex">
                    <span className="bg-gray-100 border border-r-0 px-3 py-2 text-gray-500 text-sm">@</span>
                    <Input
                      value={settings.instagram_handle}
                      onChange={(e) => setSettings({...settings, instagram_handle: e.target.value.replace('@', '')})}
                      placeholder="IhrBenutzername"
                      className="rounded-none rounded-l-none"
                    />
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Twitter className="h-4 w-4 text-sky-500" />
                    Twitter / X Benutzername
                  </Label>
                  <div className="flex">
                    <span className="bg-gray-100 border border-r-0 px-3 py-2 text-gray-500 text-sm">@</span>
                    <Input
                      value={settings.twitter_handle}
                      onChange={(e) => setSettings({...settings, twitter_handle: e.target.value.replace('@', '')})}
                      placeholder="IhrBenutzername"
                      className="rounded-none rounded-l-none"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Ihr Twitter/X Benutzername für Produktlinks und Updates
                  </p>
                </div>

                <div className="pt-4 border-t">
                  <Label className="mb-3 block">Standard-Hashtags für Posts</Label>
                  <Input
                    value={settings.default_hashtags}
                    onChange={(e) => setSettings({...settings, default_hashtags: e.target.value})}
                    placeholder="#shop #handmade #geschenk"
                    className="rounded-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Diese Hashtags werden automatisch zu geteilten Inhalten hinzugefügt
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Share Tab */}
          <TabsContent value="share">
            <Card className="rounded-none">
              <CardHeader>
                <CardTitle className="text-lg">Inhalte teilen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Kopieren Sie diese Links und teilen Sie sie auf Ihren Social-Media-Kanälen:
                </p>

                {/* Shop Link */}
                <div className="p-4 bg-gray-50 border rounded-none">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm">Shop-Startseite</span>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="rounded-none"
                      onClick={() => copyToClipboard(shopUrl, 'shop')}
                    >
                      {copied === 'shop' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <code className="text-xs bg-white p-2 block border rounded-none break-all">
                    {shopUrl}
                  </code>
                </div>

                {/* Products Link */}
                <div className="p-4 bg-gray-50 border rounded-none">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm">Alle Produkte</span>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="rounded-none"
                      onClick={() => copyToClipboard(`${shopUrl}/products`, 'products')}
                    >
                      {copied === 'products' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <code className="text-xs bg-white p-2 block border rounded-none break-all">
                    {shopUrl}/products
                  </code>
                </div>

                {/* Blog Link */}
                <div className="p-4 bg-gray-50 border rounded-none">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm">Blog</span>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="rounded-none"
                      onClick={() => copyToClipboard(`${shopUrl}/blog`, 'blog')}
                    >
                      {copied === 'blog' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <code className="text-xs bg-white p-2 block border rounded-none break-all">
                    {shopUrl}/blog
                  </code>
                </div>

                {/* Quick Share Buttons */}
                <div className="pt-4 border-t">
                  <p className="font-medium text-sm mb-3">Schnell teilen:</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 rounded-none"
                      onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shopUrl)}`, '_blank')}
                    >
                      <Facebook className="h-4 w-4 mr-2" />
                      Facebook
                    </Button>
                    <Button
                      size="sm"
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-none"
                      onClick={() => {
                        navigator.clipboard.writeText(shopUrl);
                        toast.info('Link kopiert! Fügen Sie ihn in Ihren Instagram-Post oder Ihre Bio ein.', { duration: 5000 });
                      }}
                    >
                      <Instagram className="h-4 w-4 mr-2" />
                      Instagram
                    </Button>
                    <Button
                      size="sm"
                      className="bg-sky-500 hover:bg-sky-600 rounded-none"
                      onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shopUrl)}&text=${encodeURIComponent('Entdecken Sie unseren Shop! ' + settings.default_hashtags)}`, '_blank')}
                    >
                      <Twitter className="h-4 w-4 mr-2" />
                      Twitter / X
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="rounded-none">
              <CardHeader>
                <CardTitle className="text-lg">Teilen-Einstellungen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">Teilen-Buttons aktivieren</Label>
                    <p className="text-xs text-gray-500">Zeigt Share-Buttons auf Produktseiten und Blog-Posts</p>
                  </div>
                  <Switch
                    checked={settings.share_enabled}
                    onCheckedChange={(checked) => setSettings({...settings, share_enabled: checked})}
                  />
                </div>

                {/* Future Features Info */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-none">
                  <div className="flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800 mb-1">Zukünftige Funktionen</p>
                      <p className="text-amber-700">
                        Automatisches Teilen neuer Produkte und Blog-Posts wird in einer zukünftigen Version verfügbar sein.
                        Dafür wird eine Facebook/Instagram API-Verbindung benötigt.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="opacity-50 pointer-events-none">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">Neue Produkte automatisch teilen</Label>
                      <p className="text-xs text-gray-500">Teilt neue Produkte automatisch auf verbundenen Kanälen</p>
                    </div>
                    <Switch checked={false} disabled />
                  </div>
                </div>

                <div className="opacity-50 pointer-events-none">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">Blog-Posts automatisch teilen</Label>
                      <p className="text-xs text-gray-500">Teilt neue Blog-Beiträge automatisch</p>
                    </div>
                    <Switch checked={false} disabled />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <Button onClick={saveSettings} disabled={saving} className="rounded-none">
            {saving ? 'Speichern...' : 'Einstellungen speichern'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaSettings;
